import React, { useEffect, useMemo, useState } from 'react';
import { routes } from '../../../../../utils/helpers/ApiClient';
import { AppBarCMS } from '../../../../basic';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import { CollapseBlock, Spinner } from '../../../../common';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert } from 'antd';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import {
  useDeletePhdIndividualPlanMutation,
  useGetPhdIndividualPlansQuery,
  useLazyGetPhdIndividualPlanDocumentQuery,
  useArchivePhdIndividualPlanDocumentMutation,
} from '../../../../../redux/services/phd';
import PhdIndividualPlansTable from './PhdIndividualPlansTable.jsx';
import { downloadBlobResponse } from '../../../../../utils/helpers/downloadBlobResponse';
import { usePhdReferenceData } from '../usePhdReferenceData.jsx';
import PhdArchiveFilter, { usePhdArchiveStatus } from '../PhdArchiveFilter.jsx';
import { getPhdApiErrorMessage } from '../utils/phdApiFeedback.js';
import useIsUserPhdAdmin from '../../../../../hooks/useIsUserPhdAdmin.jsx';

const PhdIndividualPlansPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAdmin = useIsUserPhdAdmin();
  const pageTitle = t('Individuálne plány');
  const { archiveStatus, setArchiveStatus } = usePhdArchiveStatus(
    'phd-individual-plans-archive-status',
  );
  const [pagination, setPagination] = useState({ page: 1, per_page: 25 });

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  const { schoolYearMap, isLoading: isReferenceLoading } = usePhdReferenceData({
    includeStudents: false,
    includeDepartments: false,
    includeStudyFields: false,
    includeUsers: false,
    includeExaminers: false,
  });

  const {
    data: plans,
    isLoading,
    error: loadError,
    refetch,
  } = useGetPhdIndividualPlansQuery(
    {
      archive_status: archiveStatus,
      paginated: 1,
      page: pagination.page,
      per_page: pagination.per_page,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );
  const [deletePlan] = useDeletePhdIndividualPlanMutation();
  const [downloadPlanDoc, { isLoading: isDownloading }] =
    useLazyGetPhdIndividualPlanDocumentQuery();
  const [archivePlanDoc, { isLoading: isArchiving }] =
    useArchivePhdIndividualPlanDocumentMutation();
  const { deleteSuccess, deleteFailed } = useToastMessages();

  const mappedPlans = useMemo(
    () =>
      (plans?.data || []).map((plan) => ({
        ...plan,
        school_year_label: schoolYearMap.get(plan.phd_school_year_id) || '—',
      })),
    [plans, schoolYearMap],
  );

  const handleDelete = async (row) => {
    try {
      await deletePlan(row.id).unwrap();
      deleteSuccess();
      refetch();
    } catch (error) {
      deleteFailed(error);
    }
  };

  const handleDownload = async (row) => {
    try {
      const response = row.is_frozen
        ? await downloadPlanDoc(row.id).unwrap()
        : await archivePlanDoc(row.id).unwrap();
      downloadBlobResponse(
        response,
        `${row.student_display_name || t('Individuálny plán')}.docx`,
      );
    } catch (error) {
      deleteFailed(error);
    }
  };

  const handleArchiveStatusChange = (value) => {
    setPagination((current) => ({ ...current, page: 1 }));
    setArchiveStatus(value);
  };

  const handlePaginationChange = (nextPagination) => {
    setPagination({
      page: nextPagination.current,
      per_page: nextPagination.pageSize,
    });
  };

  if (isLoading || isReferenceLoading) {
    return <Spinner />;
  }

  if (loadError) {
    return (
      <Alert
        type="error"
        showIcon
        title={getPhdApiErrorMessage(
          loadError,
          t(
            'PhD údaje sa nepodarilo načítať. Obnovte stránku alebo skúste znova.',
          ),
        )}
      />
    );
  }

  return (
    <>
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsDashboard}
        backUrlTooltip={t('Nástenka')}
        createText={t('Pridať individuálny plán')}
        createUrl={routes.cmsPhdIndividualPlansCreate}
        permissionsBasic="phd-individual-plans"
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako funguje individuálny študijný plán?')}
          description={t(
            'Plán platí počas celého doktorandského štúdia. Oprávnený používateľ v ňom určí predmety a úlohy; dokončený plán umožní nadväzujúce prihlášky a hodnotenia.',
          )}
        />
        <PhdArchiveFilter
          archiveStatus={archiveStatus}
          onChange={handleArchiveStatusChange}
          storageKey="phd-individual-plans-archive-status"
        />
        <CollapseBlock>
          <PhdIndividualPlansTable
            individualPlans={mappedPlans}
            onDetailClick={(row) =>
              navigate(`${routes.cmsPhdIndividualPlansEdit}/${row.id}`)
            }
            onDelete={handleDelete}
            onDownload={handleDownload}
            canDownloadRow={(row) => Boolean(row?.is_frozen || isAdmin)}
            isDownloading={isDownloading || isArchiving}
            pagination={{
              current: plans?.current_page ?? pagination.page,
              pageSize: plans?.per_page ?? pagination.per_page,
              total: plans?.total ?? 0,
              showSizeChanger: true,
              pageSizeOptions: [10, 25, 50, 100],
            }}
            onPaginationChange={handlePaginationChange}
          />
        </CollapseBlock>
      </div>
    </>
  );
};

export default PhdIndividualPlansPage;
